import type { PanZoom } from "panzoom";

export type PanzoomModuleCtx = {
    instance: PanZoom;
    svgRoot: SVGSVGElement;
    root: Element;
};

export type PanzoomModule = (ctx: PanzoomModuleCtx) => () => void;

const DATA_HREF_RE = /^data-href-(\d+)$/;

type NodeInfo = {
    el: Element;
    widths: number[];
    hrefByWidth: Map<number, string>;
};

function parseHrefVariants(el: Element): Pick<NodeInfo, 'widths' | 'hrefByWidth'> {
    const hrefByWidth = new Map<number, string>();

    for (const name of el.getAttributeNames()) {
        const match = name.match(DATA_HREF_RE);
        if (!match) {
            continue;
        }

        const width = parseInt(match[1], 10);
        const href = el.getAttribute(name);
        if (href) {
            hrefByWidth.set(width, href);
        }
    }

    const widths = [...hrefByWidth.keys()].sort((a, b) => a - b);
    return { widths, hrefByWidth };
}

function widthForHref(node: NodeInfo, href: string | null): number | null {
    if (!href) {
        return null;
    }
    for (const [width, url] of node.hrefByWidth) {
        if (url === href) {
            return width;
        }
    }
    return null;
}

/** Largest tier that covers needed CSS×DPR pixels (or smallest if none yet). */
function pickHrefForDisplayWidth(node: NodeInfo, displayWidth: number): string | null {
    const { widths, hrefByWidth } = node;
    if (widths.length === 0) {
        return null;
    }

    const neededWidth = Math.ceil(displayWidth * devicePixelRatio);
    let best = widths[0];
    for (const w of widths) {
        if (w <= neededWidth) {
            best = w;
        } else {
            break;
        }
    }

    return hrefByWidth.get(best) ?? null;
}

export const attachImageUpdater: PanzoomModule = ({ instance, svgRoot, root }) => {
    const nodes: NodeInfo[] = [...root.querySelectorAll('image')].map(el => ({
        el,
        ...parseHrefVariants(el),
    }));
    const loadedUrls = new Set<string>();
    const inflight = new Map<Element, { url: string; img: HTMLImageElement }>();

    let taskId = 0;
    function scheduleUpdate() {
        if (taskId) {
            cancelAnimationFrame(taskId);
        }
        taskId = requestAnimationFrame(() => {
            taskId = 0;
            updateNodes();
        });
    }

    function setHref(el: Element, url: string) {
        el.setAttribute('href', url);
        el.setAttribute('xlink:href', url);
    }

    function cancelLoad(el: Element) {
        const pending = inflight.get(el);
        if (!pending) {
            return;
        }
        pending.img.onload = null;
        pending.img.onerror = null;
        pending.img.src = '';
        inflight.delete(el);
    }

    function loadHref(el: Element, url: string) {
        if (loadedUrls.has(url)) {
            cancelLoad(el);
            setHref(el, url);
            return;
        }

        const pending = inflight.get(el);
        if (pending?.url === url) {
            return;
        }

        if (pending) {
            pending.img.onload = null;
            pending.img.onerror = null;
            pending.img.src = '';
            inflight.delete(el);
        }

        const img = new Image();
        inflight.set(el, { url, img });

        img.onload = () => {
            if (inflight.get(el)?.url !== url) {
                return;
            }
            loadedUrls.add(url);
            inflight.delete(el);
            setHref(el, url);
        };

        img.onerror = () => {
            if (inflight.get(el)?.url !== url) {
                return;
            }
            inflight.delete(el);
        };

        img.src = url;
    }

    function updateNodes() {
        const viewRect = svgRoot.getBoundingClientRect();

        // Read pass: gather all layout reads before any DOM writes to avoid
        // read-after-write layout thrashing. A null url means cancel.
        const work: { el: Element; url: string | null }[] = [];

        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            const { el } = node;

            const r = el.getBoundingClientRect();
            const currentHref = el.getAttribute('href');
            const nextHref = pickHrefForDisplayWidth(node, r.width);

            if (!nextHref || currentHref === nextHref) {
                continue;
            }

            // Sticky upgrade: never swap to a smaller tier once a larger one is showing.
            const currentWidth = widthForHref(node, currentHref);
            const nextWidth = widthForHref(node, nextHref);
            if (
                currentWidth !== null &&
                nextWidth !== null &&
                nextWidth <= currentWidth
            ) {
                continue;
            }

            const isVisible = !(
                r.right < viewRect.left ||
                r.left > viewRect.right ||
                r.bottom < viewRect.top ||
                r.top > viewRect.bottom
            );

            work.push({ el, url: isVisible ? nextHref : null });
        }

        // Write pass: apply loads/cancels; no layout reads happen here.
        for (const { el, url } of work) {
            if (url === null) {
                cancelLoad(el);
            } else {
                loadHref(el, url);
            }
        }
    }

    instance.on('zoom', scheduleUpdate);
    instance.on('pan', scheduleUpdate);
    window.addEventListener('resize', scheduleUpdate);

    updateNodes();

    return () => {
        window.removeEventListener('resize', scheduleUpdate);
        if (taskId) {
            cancelAnimationFrame(taskId);
            taskId = 0;
        }
        instance.off('zoom', scheduleUpdate);
        instance.off('pan', scheduleUpdate);
        for (const el of [...inflight.keys()]) {
            cancelLoad(el);
        }
    };
};
